import { useMemo } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";
import { ProfileComparisonEntry, CategoryBreakdownEntry } from "../../../api/scenarios";

interface ProfileComparisonChartProps {
  profiles: ProfileComparisonEntry[];
  domain: "health" | "biodiversity" | "economic";
}

const DOMAIN_CONFIG = {
  health: {
    title: "Human Health Impact",
    unit: "DALYs",
    axisLabel: "Disability Adjusted Life Years (DALYs)",
    breakdownKey: "health_breakdown" as const,
    heroFormat: (v: number) => v.toLocaleString(undefined, { maximumFractionDigits: 1 }),
    heroLabel: (label: string) => `DALYs Saved (${label})`,
    heroColor: "text-yellow-400",
    colors: ["#fde68a", "#f59e0b", "#92400e"],
  },
  biodiversity: {
    title: "Biodiversity Impact",
    unit: "PDFs",
    axisLabel: "Potentially Disappeared Fraction (PDF)",
    breakdownKey: "biodiversity_breakdown" as const,
    heroFormat: (v: number) => v.toFixed(3),
    heroLabel: (label: string) => `PDFs Saved (${label})`,
    heroColor: "text-blue-400",
    colors: ["#93c5fd", "#3b82f6", "#1e3a8a"],
  },
  economic: {
    title: "Economic Impact",
    unit: "USD",
    axisLabel: "USD",
    breakdownKey: "economic_breakdown" as const,
    heroFormat: (v: number) => {
      if (Math.abs(v) >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
      if (Math.abs(v) >= 1_000) return `$${(v / 1_000).toFixed(0)}K`;
      return `$${v.toFixed(0)}`;
    },
    heroLabel: (label: string) => `Economic Benefit (${label})`,
    heroColor: "text-green-400",
    colors: ["#86efac", "#22c55e", "#166534"],
  },
};

// Sort profiles from most conservative (highest P-number) to most optimistic (lowest)
const PROFILE_ORDER = ["P95", "P90", "P75", "P50"];

function sortProfilesByConservativeness(profiles: ProfileComparisonEntry[]): ProfileComparisonEntry[] {
  return [...profiles]
    .filter((p) => p.status === "completed")
    .sort((a, b) => {
      const orderA = PROFILE_ORDER.indexOf(a.profile_label);
      const orderB = PROFILE_ORDER.indexOf(b.profile_label);
      // Known labels sort by PROFILE_ORDER; unknowns sort by their total value (ascending)
      if (orderA !== -1 && orderB !== -1) return orderA - orderB;
      if (orderA !== -1) return -1;
      if (orderB !== -1) return 1;
      return 0;
    });
}

export function ProfileComparisonChart({
  profiles,
  domain,
}: ProfileComparisonChartProps) {
  const config = DOMAIN_CONFIG[domain];

  const sorted = useMemo(() => sortProfilesByConservativeness(profiles), [profiles]);

  // Build category-centric data with delta segments
  const { chartData, segmentKeys, segmentColors } = useMemo(() => {
    if (sorted.length === 0) return { chartData: [], segmentKeys: [], segmentColors: [] };

    // Collect all categories and their values per profile
    const categoryMap = new Map<string, Map<string, number>>();

    for (const profile of sorted) {
      if (domain === "economic") {
        // Economic: combine USD values from all three breakdowns (matching single-profile report logic)
        const combined = new Map<string, number>();
        for (const bk of ["health_breakdown", "biodiversity_breakdown", "economic_breakdown"] as const) {
          const bd = profile[bk];
          if (!bd || bd.length === 0) continue;
          for (const entry of bd) {
            if (entry.usd) {
              combined.set(entry.category, (combined.get(entry.category) || 0) + entry.usd);
            }
          }
        }
        for (const [cat, val] of combined) {
          if (!categoryMap.has(cat)) categoryMap.set(cat, new Map());
          categoryMap.get(cat)!.set(profile.profile_label, val);
        }
      } else {
        const breakdown = profile[config.breakdownKey];
        if (!breakdown || breakdown.length === 0) continue;
        for (const entry of breakdown) {
          if (!categoryMap.has(entry.category)) categoryMap.set(entry.category, new Map());
          categoryMap.get(entry.category)!.set(profile.profile_label, entry.value || 0);
        }
      }
    }

    if (categoryMap.size === 0) return { chartData: [], segmentKeys: [], segmentColors: [] };

    // Build segment keys: base profile, then deltas for subsequent profiles
    // sorted[0] = most conservative (base), sorted[1..n] = incremental deltas
    const keys: string[] = [];
    const colors: string[] = [];

    keys.push(sorted[0].profile_label);
    colors.push(config.colors[0] || "#86efac");

    for (let i = 1; i < sorted.length; i++) {
      keys.push(sorted[i].profile_label);

      colors.push(config.colors[Math.min(i, config.colors.length - 1)] || "#166534");
    }

    // Build chart data: one row per category, sorted by most optimistic (last profile) total value
    const data: Record<string, any>[] = [];

    for (const [category, profileValues] of categoryMap) {
      const row: Record<string, any> = { category };

      // Base segment: most conservative profile value
      const baseLabel = sorted[0].profile_label;
      const baseVal = profileValues.get(baseLabel) || 0;
      row[baseLabel] = baseVal;

      // Delta segments (keyed by profile label, not "delta" suffix)
      let prevVal = baseVal;
      for (let i = 1; i < sorted.length; i++) {
        const curLabel = sorted[i].profile_label;
        const curVal = profileValues.get(curLabel) || 0;
        row[curLabel] = curVal - prevVal;
        prevVal = curVal;
      }

      // Store the most optimistic total for sorting
      const lastProfile = sorted[sorted.length - 1].profile_label;
      row._sortValue = profileValues.get(lastProfile) || 0;

      data.push(row);
    }

    // Sort categories by absolute magnitude (largest first, regardless of sign)
    data.sort((a, b) => Math.abs(b._sortValue) - Math.abs(a._sortValue));

    return { chartData: data, segmentKeys: keys, segmentColors: colors };
  }, [sorted, config.breakdownKey, config.colors, domain]);

  if (sorted.length === 0) return null;

  // Hero metric: most conservative (highest confidence) profile's total
  const heroProfile = sorted[0];
  const heroValue = heroProfile
    ? domain === "economic"
      ? heroProfile.usd || 0
      : domain === "biodiversity"
        ? heroProfile.pdfs || 0
        : heroProfile.dalys || 0
    : 0;
  const heroLabel = heroProfile ? heroProfile.profile_label : "";

  // If no breakdowns, fall back to simple total bars
  const hasBreakdowns = chartData.length > 0;

  const simpleFallbackData = sorted.map((p) => ({
    name: p.profile_label,
    value: Math.abs(
      domain === "economic"
        ? p.usd || 0
        : domain === "biodiversity"
          ? p.pdfs || 0
          : p.dalys || 0
    ),
  }));

  // Dynamic height: ~36px per category row + margin
  const chartHeight = hasBreakdowns
    ? Math.max(200, chartData.length * 36 + 80)
    : Math.max(180, sorted.length * 50 + 60);

  // X-axis tick formatter
  const tickFormatter = (v: number) => {
    if (domain === "economic") {
      if (Math.abs(v) >= 1_000_000) return `$${(v / 1_000_000).toFixed(0)}M`;
      if (Math.abs(v) >= 1_000) return `$${(v / 1_000).toFixed(0)}K`;
      return `$${v}`;
    }
    if (Math.abs(v) >= 1000) return v.toLocaleString(undefined, { maximumFractionDigits: 0 });
    if (Math.abs(v) >= 1) return v.toLocaleString(undefined, { maximumFractionDigits: 1 });
    return v.toLocaleString(undefined, { maximumFractionDigits: 3 });
  };

  // Tooltip value formatter
  const tooltipFormatter = (value: number, name: string) => {
    const formatted = domain === "economic"
      ? `$${value.toLocaleString(undefined, { maximumFractionDigits: 0 })}`
      : value.toLocaleString(undefined, { maximumFractionDigits: 3 });
    return [formatted, name];
  };

  return (
    <div className="rounded-lg border border-border bg-surface-700 p-5">
      <div className="flex items-center justify-between mb-1">
        <h4 className="text-sm font-semibold text-text-primary">
          {config.title}
        </h4>
        <div className="text-right">
          <div className={`text-xl font-bold ${config.heroColor}`}>
            {config.heroFormat(heroValue)}
          </div>
          <div className="text-[10px] text-text-muted">{config.heroLabel(heroLabel)}</div>
        </div>
      </div>

      <div style={{ minHeight: 200 }}>
        <ResponsiveContainer width="100%" height={chartHeight}>
          {hasBreakdowns ? (
            <BarChart
              data={chartData}
              layout="vertical"
              margin={{ top: 5, right: 20, left: 10, bottom: 25 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" horizontal={false} />
              <XAxis
                type="number"
                tick={{ fill: "#9ca3af", fontSize: 11 }}
                tickFormatter={tickFormatter}
                label={{
                  value: config.axisLabel,
                  position: "insideBottom",
                  offset: -15,
                  style: { fill: "#6b7280", fontSize: 10 },
                }}
              />
              <YAxis
                type="category"
                dataKey="category"
                tick={{ fill: "#e5e7eb", fontSize: 11 }}
                width={180}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: "#1f2937",
                  border: "1px solid #374151",
                  borderRadius: 8,
                  fontSize: 12,
                }}
                labelStyle={{ color: "#e5e7eb", fontWeight: 600, marginBottom: 4 }}
                formatter={tooltipFormatter}
              />
              <ReferenceLine x={0} stroke="rgba(255,255,255,0.1)" />
              {segmentKeys.map((key, i) => (
                <Bar
                  key={key}
                  dataKey={key}
                  stackId="stack"
                  fill={segmentColors[i]}
                  radius={i === segmentKeys.length - 1 ? [0, 4, 4, 0] : undefined}
                  maxBarSize={24}
                />
              ))}
            </BarChart>
          ) : (
            <BarChart
              data={simpleFallbackData}
              layout="vertical"
              margin={{ top: 5, right: 20, left: 10, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" horizontal={false} />
              <XAxis type="number" tick={{ fill: "#9ca3af", fontSize: 11 }} />
              <YAxis
                type="category"
                dataKey="name"
                tick={{ fill: "#e5e7eb", fontSize: 13, fontWeight: 600 }}
                width={45}
              />
              <Bar dataKey="value" fill="#3b82f6" radius={[0, 4, 4, 0]} maxBarSize={28} />
            </BarChart>
          )}
        </ResponsiveContainer>
      </div>

      {/* Profile segment legend */}
      {hasBreakdowns && segmentKeys.length > 0 && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-3 pt-3 border-t border-border/50">
          {segmentKeys.map((key, i) => (
            <div key={key} className="flex items-center gap-1.5">
              <span
                className="inline-block w-2.5 h-2.5 rounded-sm flex-shrink-0"
                style={{ backgroundColor: segmentColors[i] }}
              />
              <span className="text-[11px] text-text-secondary whitespace-nowrap">{key}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
